import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { CodeSnippet } from '@/components/CodeSnippet';
import { Button } from '@/components/ui/Button';
import { ROUTE_PATHS } from '@/config/constants';
import { useAuth } from '@/contexts/AuthContext';

export function RecommendationIntegrationGuide() {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.Personalization.components.RecommendationImplementationGuide',
  });
  const { user } = useAuth();

  const navigate = useNavigate();

  function handleGenerateToken() {
    if (user?.customer.freemium) {
      toast.error(t('howItWorks.freemiumErrorMessage'));
      return;
    }

    navigate(`${ROUTE_PATHS.SETTINGS}?tab=app-tokens`);
  }

  return (
    <div className="border-t border-gray-200 bg-gray-50 p-6">
      <div className="mt-6 rounded-lg border bg-white p-6 shadow-sm">
        <div className="mb-6">
          <h4 className="text-md mb-2 font-medium">{t('endpoint.label')}</h4>
          <div className="text-md rounded-md bg-gray-800 p-4 text-white">
            {t('endpoint.method')}{' '}
            https://catalog.api.fashionai.dev/api/v1/products/protected/recommendation
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-md mb-2 font-medium">{t('howItWorks.label')}</h4>
          <p className="mb-4 text-gray-600">{t('howItWorks.description')}</p>

          <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 p-4">
              <h5 className="mb-2 font-medium">
                {t('howItWorks.requestHeaders.label')}
              </h5>
              <div className="flex flex-col gap-2 rounded-md bg-gray-50 p-3 font-mono text-sm">
                <span>{t('howItWorks.requestHeaders.header1')}</span>
                <span>{t('howItWorks.requestHeaders.header2')}</span>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <Button variant="outline" onClick={handleGenerateToken}>
              {t('howItWorks.generateTokenButton')}
            </Button>
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-md mb-2 font-medium">
            {t('requestBodyExample.label')}
          </h4>
          <div className="text-md">
            <CodeSnippet
              language="javascript"
              codeString={t('requestBodyExample.code')}
            />
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-md mb-2 font-medium">
            {t('responseOkExample.label')}
          </h4>
          <div className="text-md">
            <CodeSnippet
              language="javascript"
              codeString={t('responseOkExample.code')}
            />
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-md mb-2 font-medium">
            {t('implementationExample.label')}
          </h4>
          <div className="text-md">
            <CodeSnippet
              language="javascript"
              codeString={t('implementationExample.code', {
                endpoint: 'https://catalog.api.fashionai.dev/api',
              })}
            />
          </div>
        </div>

        <div>
          <h4 className="text-md mb-2 font-medium">
            {t('integrationTips.label')}
          </h4>
          <ul className="list-disc space-y-2 pl-5 text-gray-600">
            <li>{t('integrationTips.tip1')}</li>
            <li>{t('integrationTips.tip2')}</li>
            <li>{t('integrationTips.tip3')}</li>
            <li>{t('integrationTips.tip4')} </li>
            <li>{t('integrationTips.tip5')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
